import React from 'react'
import { Button } from '@mui/material'
import { PuntoColor } from './columnasPrecios'

/**
 * Paso 1 del bono: los vehículos elegidos, cada uno en su tarjeta con SUS colores como chips que se
 * prenden/apagan. Misma numeración y estilo que el paso 4 (legal, pieza y cobertura por vehículo), para
 * que «Vehículo 1» sea el mismo arriba y abajo.
 *
 * vehiculos:  [{ clave, etiqueta, mats: [{ material, color }] }]
 * incluidos:  Set de materiales que van en el bono.
 * sueltos:    materiales del bono que no están en el catálogo (edición de bonos viejos).
 * pendientes: motos que aún no están en el catálogo, agrupadas por vehículo (F98/F99):
 *             [{ clave, etiqueta, origen: 'lucia' | 'tp', mats: [{ material, color, sku }] }].
 *             Las de Lucia se quitan aquí (por código o el vehículo entero); las de TP son de solo lectura.
 */
const VehiculosDelBono = ({
  vehiculos = [], incluidos = new Set(), sueltos = [], pendientes = [], editable = false,
  onToggle = null, onQuitar = null, onQuitarPendiente = null, onQuitarVehiculoPendiente = null,
}) => {
  if (!vehiculos.length && !sueltos.length && !pendientes.length) return null
  const total = vehiculos.length + (sueltos.length ? 1 : 0) + pendientes.length

  const chip = (m, on) => (
    <button
      key={m.material}
      type="button"
      disabled={!editable}
      onClick={() => onToggle && onToggle(m)}
      title={on ? 'Incluido en el bono · clic para quitarlo' : 'No incluido · clic para agregarlo'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, borderRadius: 14, padding: '3px 10px',
        margin: '3px 6px 3px 0', cursor: editable ? 'pointer' : 'default', fontFamily: 'inherit',
        border: `1px solid ${on ? '#2f5b8f' : '#d5dbe2'}`, background: on ? '#e8f0fa' : '#fff',
        color: on ? '#0f2439' : '#9aa5b1', textDecoration: on ? 'none' : 'line-through',
      }}
    >
      <span style={{ fontWeight: 700 }}>{on ? '✓' : '+'}</span>
      <PuntoColor nombre={m.color} />
      {m.color || 'sin color'}
      <span style={{ fontFamily: 'monospace', color: on ? '#56606b' : '#b5bdc6' }}>{m.material}</span>
    </button>
  )

  // Código de una moto que aún no está en Lucia: de Lucia se puede quitar (✕); de TP solo se muestra.
  const chipPendiente = (m, quitable) => (
    <span
      key={m.material || m.sku}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, borderRadius: 14, padding: '3px 10px',
        margin: '3px 6px 3px 0', border: '1px solid #f0d089', background: '#fffaf0', color: '#5c3d00',
      }}
    >
      {m.color && <><PuntoColor nombre={m.color} />{m.color}</>}
      <span style={{ fontFamily: 'monospace', color: '#8a6d3b' }}>{m.material || `SKU ${m.sku}`}</span>
      {quitable && (
        <button
          type="button"
          title="Quitar este código del bono"
          onClick={() => onQuitarPendiente(m.material)}
          style={{ border: 'none', background: 'none', color: '#c53030', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}
        >
          ✕
        </button>
      )}
    </span>
  )

  const encabezado = (titulo, i, extra, accion, pendiente = false) => (
    <div
      style={{
        background: pendiente ? '#fff8e8' : '#f3f6fa', padding: '8px 12px', display: 'flex', alignItems: 'center',
        gap: 8, flexWrap: 'wrap', borderBottom: `1px solid ${pendiente ? '#f3dfae' : '#e3e7ec'}`,
      }}
    >
      {total > 1 && (
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: pendiente ? '#b7791f' : '#2f5b8f', borderRadius: 4, padding: '1px 7px' }}>
          Vehículo {i + 1} de {total}
        </span>
      )}
      <b style={{ fontSize: 13.5, color: '#0f2439' }}>{titulo}</b>
      {extra}
      {accion}
    </div>
  )

  const tarjeta = (titulo, i, mats, clave) => {
    const n = mats.filter((m) => incluidos.has(m.material)).length
    return (
      <div
        key={clave}
        style={{
          border: '1px solid #dfe5ec', borderLeft: `5px solid ${n ? '#2f5b8f' : '#d64545'}`, borderRadius: 10,
          background: '#fff', marginTop: 10, overflow: 'hidden',
        }}
      >
        {encabezado(
          titulo,
          i,
          <span style={{ fontSize: 12, color: n ? '#56606b' : '#d64545' }}>· {n} de {mats.length} color(es) incluidos</span>,
          editable && onQuitar && clave !== '__sueltos' && (
            <Button size="small" color="error" variant="text" sx={{ ml: 'auto' }} onClick={() => onQuitar(clave)}>
              Quitar vehículo
            </Button>
          ),
        )}
        <div style={{ padding: '6px 12px 8px' }}>
          {mats.map((m) => chip(m, incluidos.has(m.material)))}
          {!n && <div style={{ fontSize: 11.5, color: '#d64545' }}>Sin colores incluidos: este vehículo no entra al bono.</div>}
        </div>
      </div>
    )
  }

  const tarjetaPendiente = (p, i) => {
    const deLucia = p.origen === 'lucia'
    return (
      <div
        key={p.clave}
        style={{ border: '1px solid #f0d089', borderLeft: '5px solid #d69e2e', borderRadius: 10, background: '#fff', marginTop: 10, overflow: 'hidden' }}
      >
        {encabezado(
          p.etiqueta,
          i,
          <span style={{ fontSize: 11, color: '#8a5a00', background: '#fff3d6', borderRadius: 10, padding: '1px 8px' }}>
            esperando catálogo{deLucia ? '' : ' · de TP'}
          </span>,
          editable && deLucia && onQuitarVehiculoPendiente && (
            <Button size="small" color="error" variant="text" sx={{ ml: 'auto' }} onClick={() => onQuitarVehiculoPendiente(p.clave)}>
              Quitar vehículo
            </Button>
          ),
          true,
        )}
        <div style={{ padding: '6px 12px 8px' }}>
          {p.mats.map((m) => chipPendiente(m, editable && deLucia && onQuitarPendiente))}
          <div style={{ fontSize: 11.5, color: '#8a6d3b', marginTop: 2 }}>
            {deLucia
              ? 'Aún no está en Lucia: ya sale en la API para estos códigos y entra sola al bono cuando llegue de SAP.'
              : 'Viene de TP y aún no está en Lucia: entra sola a este bono cuando llegue.'}
          </div>
        </div>
      </div>
    )
  }

  // Orden: catálogo, sueltos, pendientes de Lucia (mismo orden que el paso 4) y al final los de TP.
  const lucia = pendientes.filter((p) => p.origen === 'lucia')
  const tp = pendientes.filter((p) => p.origen !== 'lucia')
  const base = vehiculos.length + (sueltos.length ? 1 : 0)

  return (
    <div>
      {vehiculos.map((v, i) => tarjeta(v.etiqueta, i, v.mats, v.clave))}
      {sueltos.length > 0 && tarjeta('Materiales que no están en el catálogo', vehiculos.length, sueltos, '__sueltos')}
      {lucia.map((p, k) => tarjetaPendiente(p, base + k))}
      {tp.map((p, k) => tarjetaPendiente(p, base + lucia.length + k))}
      {editable && (vehiculos.length > 0 || sueltos.length > 0) && (
        <div style={{ fontSize: 11.5, color: '#8a94a0', marginTop: 6 }}>
          Clic en un color para incluirlo o quitarlo. Al agregar un vehículo entran todos sus colores.
        </div>
      )}
    </div>
  )
}

export default VehiculosDelBono
